import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, ExpertGate } from "@/components/ProtectedRoute";
import { CookieBanner } from "@/components/CookieBanner";

import Landing from "@/pages/Landing";
import Login, { Register } from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import ExpertRegister from "@/pages/ExpertRegister";
import ExpertPending from "@/pages/ExpertPending";
import AuthCallback from "@/pages/AuthCallback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyCenter from "@/pages/PrivacyCenter";
import BookDemo from "@/pages/BookDemo";

import Onboarding from "@/pages/Onboarding";
import HrDashboard from "@/pages/HrDashboard";
import HrNetwork from "@/pages/HrNetwork";
import HrEmployees from "@/pages/HrEmployees";
import HrApprovals from "@/pages/HrApprovals";
import RatesBudget from "@/pages/RatesBudget";

import EmployeeDashboard from "@/pages/EmployeeDashboard";
import EmployeeGoals from "@/pages/EmployeeGoals";
import ExpertDirectory from "@/pages/ExpertDirectory";
import ExpertProfile from "@/pages/ExpertProfile";

import ExpertDashboard from "@/pages/ExpertDashboard";
import ExpertAvailability from "@/pages/ExpertAvailability";
import ExpertProfileEditor from "@/pages/ExpertProfileEditor";
import ExpertNetworks from "@/pages/ExpertNetworks";

import AdminDashboard from "@/pages/AdminDashboard";

const hr = (el) => <ProtectedRoute roles={["hr"]}>{el}</ProtectedRoute>;
const employee = (el) => <ProtectedRoute roles={["employee"]}>{el}</ProtectedRoute>;
const expert = (el) => <ProtectedRoute roles={["expert"]}>{el}</ProtectedRoute>;
const expertApproved = (el) => <ProtectedRoute roles={["expert"]}><ExpertGate>{el}</ExpertGate></ProtectedRoute>;
const admin = (el) => <ProtectedRoute roles={["admin"]}>{el}</ProtectedRoute>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/register" element={<Navigate to="/register/company" replace />} />
      <Route path="/register/company" element={<Register mode="company" />} />
      <Route path="/register/expert" element={<ExpertRegister />} />
      <Route path="/demo" element={<BookDemo />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/account/privacy" element={<ProtectedRoute><PrivacyCenter /></ProtectedRoute>} />

      <Route path="/onboarding" element={hr(<Onboarding />)} />
      <Route path="/hr" element={hr(<HrDashboard />)} />
      <Route path="/hr/network" element={hr(<HrNetwork />)} />
      <Route path="/hr/employees" element={hr(<HrEmployees />)} />
      <Route path="/hr/approvals" element={hr(<HrApprovals />)} />
      <Route path="/hr/rates" element={hr(<RatesBudget />)} />

      <Route path="/app" element={employee(<EmployeeDashboard />)} />
      <Route path="/app/goals" element={employee(<EmployeeGoals />)} />
      <Route path="/experts" element={employee(<ExpertDirectory />)} />
      <Route path="/experts/:expertId" element={employee(<ExpertProfile />)} />

      <Route path="/expert" element={expertApproved(<ExpertDashboard />)} />
      <Route path="/expert/availability" element={expertApproved(<ExpertAvailability />)} />
      <Route path="/expert/networks" element={expertApproved(<ExpertNetworks />)} />
      <Route path="/expert/profile" element={expert(<ExpertProfileEditor />)} />
      <Route path="/expert/pending" element={expert(<ExpertPending />)} />

      <Route path="/admin" element={admin(<AdminDashboard />)} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <CookieBanner />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
